// Pure render-state assembly + top-level draw orchestration for GameScene.
// Owns no state — given the scene snapshot it builds BoardRenderState /
// NumpadRenderState / StatusBarRenderState and paints the win banner.
// Lives apart from GameScene to keep that file inside the 200-line cap.

import { computeDigitCounts } from '../render/NumpadRenderer.js';
import { getBorderWoodPattern } from '@bn-games/render-helpers';
import {
  BOARD_CONTROLS_SEAM_PX,
  BOARD_FRAME_BEVEL_PX,
  STOPWATCH_MAX_MS,
  WIN_BANNER_FONT_PX,
} from '../utils/constants.js';
import { formatTime } from '../ui/settingsRows.js';
import type { GameStateData } from '../game/GameState.js';
import type { Grid, NonEmptyDigit } from '../game/types.js';
import type { Layout, NumpadButtonId } from '../layout/types.js';
import type { Theme } from '../settings/themes.js';
import type {
  BoardRenderState,
  HintHighlight,
  NumpadActionLabels,
  NumpadRenderState,
} from '../render/types.js';
import type { BoardRenderer } from '../render/BoardRenderer.js';
import type { NumpadRenderer } from '../render/NumpadRenderer.js';
import type { StatColumn, StatusBarRenderState } from '../render/StatusBarRenderer.js';
import type { StatusBarRenderer } from '../render/StatusBarRenderer.js';

const FONT_FAMILY = '"Segoe UI", system-ui, -apple-system, sans-serif';

/** Mutable scene view-state needed to assemble render snapshots. */
export interface SceneViewState {
  readonly selectedIndex: number | null;
  readonly pencilMode: boolean;
  readonly errorFlash: ReadonlySet<number>;
  readonly pressedButton: NumpadButtonId | null;
  readonly highlightErrors: boolean;
  readonly winBannerLabel: string | null;
  readonly activeHint: HintHighlight | null;
  /** Numpad-tap digit latch. When the selected cell has no digit, this is
   *  what drives the same-digit highlight (cell wash + pencil emphasis). */
  readonly focusedDigitLatch: NonEmptyDigit | null;
  /** True while the pause menu is open. The status bar reads this to flag
   *  the pause button as latched-on. */
  readonly paused: boolean;
  /** True for the brief window between pointerdown and pointerup on the
   *  pause button. Currently unused (no down-state press in v1) but kept
   *  for symmetry with the numpad. */
  readonly pressedPause: boolean;
}

/** Localized strings the renderer needs but doesn't own. The scene reads
 *  these once via i18n and passes them in. */
export interface SceneRenderStrings {
  readonly statusGameLabel: string;
  readonly statusErrorsLabel: string;
  readonly statusTimeLabel: string;
  readonly difficultyLabels: Readonly<Record<'easy' | 'medium' | 'hard', string>>;
  readonly actionLabels: NumpadActionLabels;
}

export interface SceneRenderDeps {
  readonly ctx: CanvasRenderingContext2D;
  readonly layout: Layout;
  readonly theme: Theme;
  readonly state: GameStateData;
  readonly view: SceneViewState;
  readonly elapsedMs: number;
  readonly strings: SceneRenderStrings;
  readonly board: BoardRenderer;
  readonly numpad: NumpadRenderer;
  readonly statusBar: StatusBarRenderer;
}

/** Format ms as `mm:ss`, clamped to `99:59` so a long session can't
 *  outgrow the timer slot. Pure — exposed for unit tests. */
export function formatStopwatch(ms: number): string {
  const clamped = Math.min(Math.max(0, ms), STOPWATCH_MAX_MS);
  return formatTime(clamped);
}

/** Resolve the digit the player is focusing. Selection wins — when the
 *  selected cell holds a value, every other instance of that value lights
 *  up. With no value at the selection, the latched numpad-tap digit fills in
 *  so taps on the controls strip can drive the highlight from an empty cell. */
function pickFocusedDigit(
  givens: Grid,
  user: Grid,
  selectedIndex: number | null,
  latch: NonEmptyDigit | null,
): NonEmptyDigit | null {
  if (selectedIndex !== null) {
    const given = givens[selectedIndex] ?? 0;
    if (given !== 0) return given as NonEmptyDigit;
    const entry = user[selectedIndex] ?? 0;
    if (entry !== 0) return entry as NonEmptyDigit;
  }
  return latch;
}

function buildBoardState(deps: SceneRenderDeps): BoardRenderState {
  const { layout, theme, state, view } = deps;
  return {
    layout,
    theme,
    givens: state.puzzle.givens,
    user: state.user,
    pencil: state.pencil,
    solution: state.puzzle.solution,
    selectedIndex: view.selectedIndex,
    focusedDigit: pickFocusedDigit(
      state.puzzle.givens,
      state.user,
      view.selectedIndex,
      view.focusedDigitLatch,
    ),
    highlightErrors: view.highlightErrors,
    errorFlash: view.errorFlash,
    activeHint: view.activeHint,
  };
}

function buildNumpadState(deps: SceneRenderDeps): NumpadRenderState {
  const { layout, theme, state, view, strings } = deps;
  return {
    layout,
    theme,
    pencilMode: view.pencilMode,
    digitCounts: computeDigitCounts(state.puzzle.givens, state.user),
    highlightDigit: pickFocusedDigit(
      state.puzzle.givens,
      state.user,
      view.selectedIndex,
      view.focusedDigitLatch,
    ),
    pressedButton: view.pressedButton,
    hintsUsed: state.hintsUsed,
    actionLabels: strings.actionLabels,
  };
}

function buildStatusBarState(deps: SceneRenderDeps): StatusBarRenderState {
  const { layout, theme, state, view, strings, elapsedMs } = deps;
  const columns: StatColumn[] = [
    {
      label: strings.statusGameLabel,
      value: strings.difficultyLabels[state.puzzle.difficulty],
    },
    {
      label: strings.statusErrorsLabel,
      value: String(state.errorsMade),
    },
    {
      label: strings.statusTimeLabel,
      value: formatStopwatch(elapsedMs),
    },
  ];
  return {
    region: layout.statusBar,
    theme,
    columns,
    paused: view.paused,
    pressedPause: view.pressedPause,
  };
}

function drawBoardFrame(deps: SceneRenderDeps): void {
  const { ctx, layout, theme } = deps;
  const t = layout.frameThickness;
  // One wood frame wraps the full stack: status-bar tray + seam + 9×9 board
  // + seam + controls tray. Status tray sits between statusBar.y and
  // statusBar.y + statusBar.height; the numpad tray sits between numpad.y
  // and numpad.y + numpad.height.
  const interiorLeft = layout.statusBar.x;
  const interiorTop = layout.statusBar.y;
  const interiorRight = layout.numpad.x + layout.numpad.width;
  const interiorBottom = layout.numpad.y + layout.numpad.height;
  const fx = interiorLeft - t;
  const fy = interiorTop - t;
  const fw = interiorRight - interiorLeft + t * 2;
  const fh = interiorBottom - interiorTop + t * 2;

  ctx.save();
  ctx.fillStyle = getBorderWoodPattern(ctx, theme, { width: fw, height: fh });
  ctx.fillRect(fx, fy, fw, fh);

  // Outer bevel — light catches top/left, shadow on bottom/right.
  const bv = BOARD_FRAME_BEVEL_PX;
  ctx.fillStyle = 'rgba(255, 220, 160, 0.22)';
  ctx.fillRect(fx, fy, fw, bv);
  ctx.fillRect(fx, fy, bv, fh);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.30)';
  ctx.fillRect(fx, fy + fh - bv, fw, bv);
  ctx.fillRect(fx + fw - bv, fy, bv, fh);

  // Inner bevel — subtle dip from frame to playing surface (top edge of grid
  // and side edges spanning grid+seam+tray).
  ctx.fillStyle = 'rgba(0, 0, 0, 0.30)';
  ctx.fillRect(interiorLeft - 1, interiorTop - 1, interiorRight - interiorLeft + 2, 1);
  ctx.fillRect(interiorLeft - 1, interiorTop - 1, 1, interiorBottom - interiorTop + 2);
  ctx.fillStyle = 'rgba(255, 220, 160, 0.12)';
  ctx.fillRect(interiorLeft - 1, interiorBottom, interiorRight - interiorLeft + 2, 1);
  ctx.fillRect(interiorRight, interiorTop - 1, 1, interiorBottom - interiorTop + 2);
  ctx.restore();
}

/** Recessed groove between two adjacent regions in the wood frame (status
 *  tray ↔ board ↔ controls tray). */
function drawSeam(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
): void {
  const sh = BOARD_CONTROLS_SEAM_PX;
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.50)';
  ctx.fillRect(x, y, width, sh - 1);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.30)';
  ctx.fillRect(x, y, width, 2);
  ctx.fillStyle = 'rgba(255, 220, 160, 0.10)';
  ctx.fillRect(x, y + sh - 1, width, 1);
  ctx.restore();
}

function drawStatusBoardSeam(deps: SceneRenderDeps): void {
  const { ctx, layout } = deps;
  drawSeam(ctx, layout.board.x, layout.statusBar.y + layout.statusBar.height, layout.board.width);
}

function drawBoardControlsSeam(deps: SceneRenderDeps): void {
  const { ctx, layout } = deps;
  drawSeam(ctx, layout.board.x, layout.board.y + layout.board.height, layout.board.width);
}

/** Bear-off-tray treatment for any region inside the wood frame — recessed
 *  well with a wood-pattern floor, depth-darken overlay, inset shadow on
 *  top/left, subtle glow on bottom/right, and a hairline outline. Used for
 *  both the status tray (top) and the controls tray (bottom) so they read
 *  as one wood frame with mirrored wells. */
function drawTrayWell(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  theme: Theme,
): void {
  ctx.save();
  ctx.fillStyle = getBorderWoodPattern(ctx, theme, { width: w, height: h });
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.40)';
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fillRect(x, y, w, 1.5);
  ctx.fillRect(x, y, 1.5, h);
  ctx.fillStyle = 'rgba(255, 220, 160, 0.10)';
  ctx.fillRect(x, y + h - 1, w, 1);
  ctx.fillRect(x + w - 1, y, 1, h);
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  ctx.restore();
}

function drawStatusTray(deps: SceneRenderDeps): void {
  const { ctx, layout, theme } = deps;
  const { x, y, width: w, height: h } = layout.statusBar;
  drawTrayWell(ctx, x, y, w, h, theme);
}

function drawControlsTray(deps: SceneRenderDeps): void {
  const { ctx, layout, theme } = deps;
  const { x, y, width: w, height: h } = layout.numpad;
  drawTrayWell(ctx, x, y, w, h, theme);
}

function drawWinBanner(deps: SceneRenderDeps): void {
  const { ctx, layout, theme, view } = deps;
  if (view.winBannerLabel === null) return;
  const cx = layout.board.x + layout.board.width / 2;
  const cy = layout.board.y + layout.board.height / 2;
  ctx.save();
  ctx.fillStyle = theme.numpadButtonActive;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${WIN_BANNER_FONT_PX}px ${FONT_FAMILY}`;
  ctx.fillText(view.winBannerLabel, cx, cy);
  ctx.restore();
}

/** Convenience entry point — wraps `drawScene` so the scene class only has
 *  to assemble its view-state, settings, and i18n strings once per frame. */
export interface RenderFrameInput {
  readonly ctx: CanvasRenderingContext2D;
  readonly layout: Layout;
  readonly theme: Theme;
  readonly state: GameStateData;
  readonly view: SceneViewState;
  readonly elapsedMs: number;
  readonly strings: SceneRenderStrings;
  readonly board: BoardRenderer;
  readonly numpad: NumpadRenderer;
  readonly statusBar: StatusBarRenderer;
}

export function renderFrame(input: RenderFrameInput): void {
  drawScene({
    ctx: input.ctx,
    layout: input.layout,
    theme: input.theme,
    state: input.state,
    view: input.view,
    elapsedMs: input.elapsedMs,
    strings: input.strings,
    board: input.board,
    numpad: input.numpad,
    statusBar: input.statusBar,
  });
}

/** Single frame: clear → frame → status tray → status seam → board → board seam → controls tray → buttons → status-bar paint → win banner. */
export function drawScene(deps: SceneRenderDeps): void {
  const { ctx, layout, theme, board, numpad, statusBar } = deps;
  ctx.save();
  ctx.fillStyle = theme.feltBase;
  ctx.fillRect(0, 0, layout.viewport.width, layout.viewport.height);
  ctx.restore();

  drawBoardFrame(deps);
  drawStatusTray(deps);
  drawStatusBoardSeam(deps);
  board.draw(ctx, buildBoardState(deps));
  drawBoardControlsSeam(deps);
  drawControlsTray(deps);
  numpad.draw(ctx, buildNumpadState(deps));
  statusBar.draw(ctx, buildStatusBarState(deps));
  drawWinBanner(deps);
}
